/** Package links — append-only edge list (source → target with a link type). */
import * as React from "react";
import { useMemo } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { useTable } from "@refinedev/react-table";
import {
  useGo,
  useNavigation,
  useDelete,
  type HttpError,
} from "@refinedev/core";
import { MoreHorizontal, Plus, Trash2 } from "@/components/ui/icon";
import type { PackageLink } from "@aos/shared";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";
import { RefLink } from "@/components/inspector";
import { formatDateTime } from "@/lib/format";

import {
  DataTable,
  PageHeader,
  SearchBox,
  useSearchFilter,
  type RefineTableCore,
} from "../projects/_shared";
import { humanize } from "../projects/_form";

export function PackageLinkList() {
  const go = useGo();
  const { showUrl, createUrl } = useNavigation();
  const { mutate: deleteLink } = useDelete();
  const [pendingDelete, setPendingDelete] = React.useState<PackageLink | null>(
    null,
  );

  const columns = useMemo<ColumnDef<PackageLink, unknown>[]>(
    () => [
      {
        id: "source_package_id",
        accessorKey: "source_package_id",
        header: "Source",
        enableSorting: false,
        cell: ({ row }) => (
          <span onClick={(e) => e.stopPropagation()}>
            <RefLink
              resource="packages"
              id={row.original.source_package_id}
              labelField="name"
            />
          </span>
        ),
      },
      {
        id: "link_type",
        accessorKey: "link_type",
        header: "Link",
        cell: ({ row }) => (
          <Badge variant="outline">{humanize(row.original.link_type)}</Badge>
        ),
      },
      {
        id: "target_package_id",
        accessorKey: "target_package_id",
        header: "Target",
        enableSorting: false,
        cell: ({ row }) => (
          <span onClick={(e) => e.stopPropagation()}>
            <RefLink
              resource="packages"
              id={row.original.target_package_id}
              labelField="name"
            />
          </span>
        ),
      },
      {
        id: "created_at",
        accessorKey: "created_at",
        header: "Created",
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {formatDateTime(row.original.created_at)}
          </span>
        ),
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions
            onShow={() => go({ to: showUrl("package-links", row.original.id) })}
            onDelete={() => setPendingDelete(row.original)}
          />
        ),
      },
    ],
    [go, showUrl],
  );

  const table = useTable<PackageLink, HttpError>({
    columns,
    refineCoreProps: {
      resource: "package-links",
      pagination: { pageSize: 25 },
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
    },
  });

  const core = table.refineCore as unknown as RefineTableCore & {
    setFilters: (
      filters: { field: string; operator: "contains"; value: string }[],
    ) => void;
  };
  const [search, setSearch] = useSearchFilter(core.setFilters);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Package Links"
        description="Typed edges between packages (uses, supports, prioritizes, …)."
        action={
          <Button onClick={() => go({ to: createUrl("package-links") })}>
            <Plus className="mr-2 h-4 w-4" /> New Link
          </Button>
        }
      />

      <SearchBox
        value={search}
        onChange={setSearch}
        placeholder="Search links…"
      />

      <DataTable
        table={table}
        core={core}
        columns={columns}
        emptyMessage="No package links yet."
        onRowClick={(l) => go({ to: showUrl("package-links", l.id) })}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open: boolean) => !open && setPendingDelete(null)}
        title="Delete link?"
        description={<>This removes the package link. This cannot be undone.</>}
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingDelete) {
            deleteLink({ resource: "package-links", id: pendingDelete.id });
          }
        }}
      />
    </div>
  );
}

function RowActions({
  onShow,
  onDelete,
}: {
  onShow: () => void;
  onDelete: () => void;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <MoreHorizontal className="h-4 w-4" />
          <span className="sr-only">Open actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={onShow}>View</DropdownMenuItem>
        <DropdownMenuItem
          className="text-destructive focus:text-destructive"
          onClick={onDelete}
        >
          <Trash2 className="mr-2 h-4 w-4" /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default PackageLinkList;
